import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiSave, FiX, FiCheckCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import tasksApi, { canEditTask } from '../../api/tasksApi';
import api from '../../finance/api/client';

const STATUS_META = {
    TO_DO: { label: 'To Do', color: '#64748b', bg: '#f1f5f9' },
    IN_PROGRESS: { label: 'In Progress', color: '#2563eb', bg: '#dbeafe' },
    COMPLETED: { label: 'Completed', color: '#16a34a', bg: '#dcfce7' },
    BLOCKED: { label: 'Blocked', color: '#e11d48', bg: '#ffe4e6' }
};

const PRIORITY_META = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
    CRITICAL: 'Critical'
};

const getTodayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getTomorrowStr = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getEndOfWeekStr = () => {
    const d = new Date();
    const day = d.getDay();
    let diff = (5 - day + 7) % 7;
    if (diff === 0 && d.getHours() >= 17) diff = 7;
    d.setDate(d.getDate() + diff);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EMPTY_TASK = { title: '', description: '', taskStatus: 'TO_DO', priority: 'MEDIUM', deadLine: getTodayStr(), username: '' };

export default function TaskEditorPage() {
    const { projectId, taskId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [taskForm, setTaskForm] = useState(EMPTY_TASK);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    const isEdit = Boolean(taskId);
    const parsedProjectId = parseInt(projectId);

    useEffect(() => {
        fetchUsers();
        if (isEdit) {
            fetchTaskDetails();
        } else {
            const me = localStorage.getItem('username') || '';
            setTaskForm(prev => ({ ...prev, username: me }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId, isEdit]);

    const fetchUsers = async () => {
        try {
            const res = await api.get(`/project/users/all?projectId=${projectId}`);
            const raw = res.data?.data || res.data;
            setUsers(Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : []);
        } catch (err) {
            console.error('Failed to fetch users', err);
        }
    };

    const fetchTaskDetails = async () => {
        try {
            const me = localStorage.getItem('username') || '';
            if (location.state?.task) {
                const task = location.state.task;
                if (!canEditTask(task, me)) {
                    toast.error('Only the task assignee or creator can edit this task.');
                    navigate(`/ProjectDetails/${projectId}`);
                    return;
                }
                setTaskForm({
                    title: task.title || '',
                    description: task.description || '',
                    taskStatus: task.taskStatus || 'TO_DO',
                    priority: task.priority || 'MEDIUM',
                    deadLine: task.deadLine || '',
                    username: task.username || ''
                });
                return;
            }

            setLoading(true);
            const projectTasks = await tasksApi.getProjectTasks(projectId);
            const task = (projectTasks || []).find(t => t.taskId === parseInt(taskId));
            
            if (task) {
                if (!canEditTask(task, me)) {
                    toast.error('Only the task assignee or creator can edit this task.');
                    navigate(`/ProjectDetails/${projectId}`);
                    return;
                }
                setTaskForm({
                    title: task.title || '',
                    description: task.description || '',
                    taskStatus: task.taskStatus || 'TO_DO',
                    priority: task.priority || 'MEDIUM',
                    deadLine: task.deadLine || '',
                    username: task.username || ''
                });
            } else {
                toast.error('Task not found');
                navigate(`/ProjectDetails/${projectId}`);
            }
        } catch (err) {
            toast.error('Failed to load task details');
            navigate(`/ProjectDetails/${projectId}`);
        } finally {
            setLoading(false);
        }
    };

    const saveTask = async () => {
        try {
            if (!taskForm.title) return toast.error('Title is required');
            
            const payload = {
                title: taskForm.title.trim(),
                description: taskForm.description.trim() || null,
                taskStatus: taskForm.taskStatus || 'TO_DO',
                priority: taskForm.priority || 'MEDIUM',
                deadLine: taskForm.deadLine || null,
                projectId: isNaN(parsedProjectId) ? null : parsedProjectId,
                username: taskForm.username || null
            };
            
            if (isEdit) {
                await tasksApi.updateTask(taskId, payload);
                toast.success('Task updated successfully');
            } else {
                await tasksApi.createTask(payload);
                toast.success('Task created successfully');
            }
            
            navigate(`/ProjectDetails/${projectId}`);
        } catch (err) {
            toast.error(err.message || 'Failed to save task.');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto py-8 px-4 animate-fade-in">

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">{isEdit ? 'Edit Task' : 'Create New Task'}</h1>
                        <p className="text-slate-500 text-sm mt-1">
                            {isEdit ? `Updating task #${taskId}` : 'Fill in the details below to add a new task to the project.'}
                        </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center text-xl">
                        <FiCheckCircle />
                    </div>
                </div>

                <div className="p-8 space-y-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Task Title *</label>
                        <input 
                            value={taskForm.title} 
                            onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} 
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800" 
                            placeholder="What needs to be done?" 
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                        <textarea 
                            rows={4} 
                            value={taskForm.description} 
                            onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} 
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800 resize-y" 
                            placeholder="Add more details, links, or context..." 
                        />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Assignee</label>
                            <select 
                                value={taskForm.username} 
                                onChange={e => setTaskForm({ ...taskForm, username: e.target.value })} 
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800"
                            >
                                <option value="">-- Select Assignee --</option>
                                {users.map(u => (
                                    <option key={u.id || u.username} value={u.username}>{u.username} ({u.role})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-semibold text-slate-700">Deadline</label>
                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setTaskForm(prev => ({ ...prev, deadLine: getTodayStr() }))}
                                        className={`px-2 py-0.5 text-xs font-semibold rounded-md border transition-all cursor-pointer ${
                                            taskForm.deadLine === getTodayStr()
                                                ? 'bg-blue-50 text-blue-700 border-blue-300 font-bold'
                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                        }`}
                                    >
                                        Today
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setTaskForm(prev => ({ ...prev, deadLine: getTomorrowStr() }))}
                                        className={`px-2 py-0.5 text-xs font-semibold rounded-md border transition-all cursor-pointer ${
                                            taskForm.deadLine === getTomorrowStr()
                                                ? 'bg-blue-50 text-blue-700 border-blue-300 font-bold'
                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                        }`}
                                    >
                                        Tomorrow
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setTaskForm(prev => ({ ...prev, deadLine: getEndOfWeekStr() }))}
                                        className={`px-2 py-0.5 text-xs font-semibold rounded-md border transition-all cursor-pointer ${
                                            taskForm.deadLine === getEndOfWeekStr()
                                                ? 'bg-blue-50 text-blue-700 border-blue-300 font-bold'
                                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                        }`}
                                    >
                                        End of Week
                                    </button>
                                </div>
                            </div>
                            <input 
                                type="date" 
                                value={taskForm.deadLine} 
                                onChange={e => setTaskForm({ ...taskForm, deadLine: e.target.value })} 
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800" 
                            />
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Priority</label>
                            <select 
                                value={taskForm.priority} 
                                onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })} 
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800 font-medium"
                            >
                                {Object.entries(PRIORITY_META).map(([k, label]) => (
                                    <option key={k} value={k}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                            <select 
                                value={taskForm.taskStatus} 
                                onChange={e => setTaskForm({ ...taskForm, taskStatus: e.target.value })} 
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800 font-medium"
                            >
                                {Object.entries(STATUS_META).map(([k, v]) => (
                                    <option key={k} value={k}>{v.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3">
                    <button 
                        onClick={() => navigate(`/ProjectDetails/${projectId}`)} 
                        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-xl hover:text-slate-800 hover:bg-slate-50 transition-colors shadow-sm"
                    >
                        <FiX /> Cancel
                    </button>
                    <button 
                        onClick={saveTask} 
                        className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-sm shadow-blue-600/20"
                    >
                        <FiSave /> {isEdit ? 'Update Task' : 'Save Task'}
                    </button>
                </div>
            </div>
        </div>
    );
}
